import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Drawer, Form, Select, Spin, message, Tag, Input, TimePicker } from 'antd';
import moment from 'moment';
import reqwest from 'reqwest';
import config from '../../config';
import { getToken, formatDoseByUnit } from '../../Utils/common';

const { Option } = Select;

function MedicationIntakes({ hospitalizationId, hospitalizationStatus, medicalSchemaId, isReadOnly }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [schemaMeds, setSchemaMeds] = useState([]);
  // Fecha de trabajo: siempre el día actual (no editable)
  const [reinforcements, setReinforcements] = useState([]);

  const MOMENT_MAP = {
    DES: 'Desayuno',
    ALM: 'Almuerzo',
    MER: 'Merienda',
    CEN: 'Cena',
    PER: 'Personalizado',
  };

  const isHospitalizationActive = hospitalizationStatus === 'A' && !isReadOnly;
  const canOperate = !!hospitalizationId && isHospitalizationActive;

  const fetchIntakes = () => {
    if (!hospitalizationId) return;
    setLoading(true);
    reqwest({
      url: `${config.api_url}/medicationintake/?hospitalization=${hospitalizationId}`,
      method: 'get',
      type: 'json',
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then(resp => {
      const results = Array.isArray(resp) ? resp : (resp.results || []);
      setItems(results);
    }).catch(() => message.error('No se pudieron obtener las tomas')).always(() => setLoading(false));
  };

  const fetchSchemaMeds = async () => {
    if (!hospitalizationId) { setSchemaMeds([]); return; }
    try {
      // Intentar traer la mayor cantidad posible por página y seguir el paginado si existiera
      const day = moment().format('YYYY-MM-DD');
      let url = `${config.api_url}/hospitalization/active-schema-medications?hospitalization=${hospitalizationId}&limit=1000&date=${encodeURIComponent(day)}`;
      const all = [];
      // Seguir next hasta agotar páginas (o si el backend devuelve lista simple)
      // Nota: usamos await dentro del bucle para simplificar el flujo
      // eslint-disable-next-line no-await-in-loop
      while (url) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await reqwest({
          url,
          method: 'get',
          type: 'json',
          headers: { 'Authorization': `Token ${getToken()}` },
        });
        if (Array.isArray(resp)) {
          all.push(...resp);
          break;
        }
        const results = resp.results || [];
        all.push(...results);
        url = resp.next || null;
      }
      setSchemaMeds(all);
    } catch (e) {
      message.error('No se pudieron obtener los medicamentos activos de la internación');
    }
  };

  const fetchReinforcements = () => {
    if (!hospitalizationId) { setReinforcements([]); return; }
    const day = moment().format('YYYY-MM-DD');
    reqwest({
      url: `${config.api_url}/hospitalizationmedreinforcement/?hospitalization=${hospitalizationId}&date=${encodeURIComponent(day)}`,
      method: 'get',
      type: 'json',
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then(resp => {
      const results = Array.isArray(resp) ? resp : (resp.results || []);
      setReinforcements(results);
    }).catch(() => {
      // keep silent to not block intakes drawer
      setReinforcements([]);
    });
  };

  useEffect(() => { fetchIntakes(); fetchReinforcements(); }, [hospitalizationId]);
  useEffect(() => { fetchSchemaMeds(); }, [hospitalizationId]);

  // Como la fecha no es editable, refrescamos al abrir el drawer y al cambiar la internación

  const openDrawer = () => {
    // Refresh latest active schema medications and reinforcements when opening the drawer
    fetchSchemaMeds();
    fetchReinforcements();
    setDrawerVisible(true);
  };
  const closeDrawer = () => { form.resetFields(); setDrawerVisible(false); };

  const onSubmit = (values) => {
    if (!hospitalizationId) return;
    // Validación: si no fue tomada, motivo requerido
    if (values.taken === false && (!values.not_taken_reason || !String(values.not_taken_reason).trim())) {
      message.warning('Indique el motivo si la toma no fue realizada');
      return;
    }

    const medSelections = values.schema_medication;

    // if (!medSelections || !Array.isArray(medSelections) || medSelections.length === 0) {
    //   // Si el array está vacío o no existe, forzamos validación visual y mostramos mensaje de advertencia
    //   form.setFields([{ name: 'schema_medication', errors: ['Seleccione al menos un medicamento'] }]);
    //   message.warning('Seleccione al menos un medicamento');
    //   return;
    // }

    setSaving(true);

    const payload = medSelections.map(val => {
      const isReinforcement = String(val).startsWith('reinf-');
      const item = {
        hospitalization: hospitalizationId,
        taken_time: values.taken_time ? moment(values.taken_time).format('HH:mm') : moment().format('HH:mm'),
        taken: values.taken !== undefined ? values.taken : true,
        not_taken_reason: values.taken === false ? values.not_taken_reason : null,
      };
      if (isReinforcement) {
        item.reinforcement = Number(String(val).replace('reinf-', ''));
      } else {
        item.schema_medication = Number(val);
      }
      return item;
    });

    reqwest({
      url: `${config.api_url}/medicationintake/bulk-create/`,
      method: 'post',
      type: 'json',
      contentType: 'application/json',
      data: JSON.stringify(payload),
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then((resp) => {
      setSaving(false);
      form.resetFields();
      setDrawerVisible(false);

      const createdCount = Array.isArray(resp) ? resp.length : (resp.created ? resp.created.length : 0);
      const errorCount = resp.errors ? resp.errors.length : 0;

      if (errorCount > 0) {
        message.warning(`Se registraron ${createdCount} tomas, pero ${errorCount} fallaron.`);
      } else {
        message.success(`${createdCount} toma(s) registrada(s)`);
      }

      fetchIntakes();
    }).catch(err => {
      setSaving(false);
      const status = (err && err.status) || '';
      message.error(`Error al registrar la(s) toma(s): ${status}`);
    });
  };

  const columns = [
    { title: 'Fecha', dataIndex: 'taken_at', key: 'taken_at', render: val => val ? moment(val).format('DD/MM/YYYY') : '-' },
    { title: 'Medicamento', dataIndex: 'medication_name', key: 'medication_name', render: (_, r) => r.medication_name || (r.schema_medication ? `#${r.schema_medication}` : '-') },
    { title: 'Dosis', dataIndex: 'dose', key: 'dose', render: (v, r) => {
      if (v == null || v === '') return '-';
      const disp = formatDoseByUnit(v, r.unit);
      return `${disp}${r.unit ? ' ' + r.unit : ''}`;
    } },
    { title: 'Horario', dataIndex: 'moment_display', key: 'moment_display', render: (val, r) => {
      if (r.moment === 'PER') return <Tag color="volcano">{r.custom_moment || 'Personalizado'}</Tag>;
      const label = MOMENT_MAP[r.moment] || r.moment || '-';
      return <Tag>{label}</Tag>;
    } },
    { title: 'Estado', dataIndex: 'taken', key: 'taken', render: (v) => v === false ? <Tag color="red">No tomada</Tag> : <Tag color="green">Tomada</Tag> },
    { title: 'Motivo', dataIndex: 'not_taken_reason', key: 'not_taken_reason', render: (v, r) => r.taken === false ? (v || '-') : '-' },
    { title: 'Registrado por', dataIndex: 'created_by_username', key: 'created_by_username', render: (v, r) => v || (r.created_by ? `#${r.created_by}` : '-') },
  ];

  return (
    <Card title="Historial de Administración de Medicamentos" extra={<Button type="primary" onClick={openDrawer} disabled={!canOperate}>Registrar medicación</Button>}>
      {!canOperate && (
        <div style={{ marginBottom: 12, color: '#999' }}>
          {!hospitalizationId ? 'Seleccione una internación con esquema activo para registrar tomas.' : 'La internación no está activa. No se pueden registrar nuevas tomas.'}
        </div>
      )}
      <Spin spinning={loading}>
        <Table rowKey={(r) => `${r.id}`} columns={columns} dataSource={items} pagination={false} />
      </Spin>

      <Drawer
        title="Registrar toma"
        width={"60%"}
        visible={drawerVisible}
        onClose={closeDrawer}
        destroyOnClose
        footer={<div style={{ textAlign: 'right' }}>
          <Button onClick={closeDrawer} style={{ marginRight: 8 }}>Cancelar</Button>
          <Button type="primary" loading={saving} onClick={() => form.submit()}>Guardar</Button>
        </div>}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={onSubmit}
          initialValues={{ taken: true, schema_medication: [] }}
        >
          <Form.Item
            name="schema_medication"
            label="Medicamento del esquema"
            rules={[{ validator: (_, value) => (!value || !Array.isArray(value) || value.length === 0) ? Promise.reject(new Error('Seleccione al menos un medicamento')) : Promise.resolve() }]}
          >
            <Select
              mode="multiple"
              placeholder="Seleccione uno o varios"
              allowClear
              dropdownMatchSelectWidth={false}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            >
              {(() => {
                // Ocultar opciones que ya fueron registradas como toma (tomadas o no) en esta hospitalización para el día seleccionado
                const takenTodaySchema = new Set(
                  items
                    .filter(it => it.taken_at && moment(it.taken_at).isSame(moment(), 'day'))
                    .map(it => (it.schema_medication != null ? String(it.schema_medication) : `reinf-${it.reinforcement}`))
                );
                const available = schemaMeds.filter(sm => !takenTodaySchema.has(String(sm.id)));
                const options = available.map(sm => {
                  const typeDisp = sm.medical_schema_type_display;
                  const typeColor = typeDisp === 'Psiquiátrico' ? 'purple' : 'blue';
                  const doseStr = sm.dose != null ? `${formatDoseByUnit(sm.dose, sm.unit)}${sm.unit ? ' ' + sm.unit : ''}` : '';
                  const text = `${sm.medication_name || `#${sm.medication}`} ${doseStr ? `— ${doseStr}` : ''} ${sm.moment ? `— ${MOMENT_MAP[sm.moment] || sm.moment}${sm.moment === 'PER' && sm.custom_moment ? ` (${sm.custom_moment})` : ''}` : ''}`;
                  return (
                    <Option key={String(sm.id)} value={String(sm.id)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{text}</span>
                        {typeDisp ? <Tag color={typeColor}>{typeDisp}</Tag> : null}
                      </div>
                    </Option>
                  );
                });

                // Agregar refuerzos de hoy como opciones seleccionables (solo mismo día)
                const reinforcementsToday = (reinforcements || []).filter(r => r.applied_at && moment(r.applied_at).isSame(moment(), 'day'));
                if (reinforcementsToday.length > 0) {
                  options.push(
                    <Option disabled key="reinf-header" value="reinf-header">— Refuerzos (hoy) —</Option>
                  );
                  reinforcementsToday.forEach(r => {
                    const doseTxt = (r.dose != null && r.dose !== '') ? `${formatDoseByUnit(r.dose, r.unit)}${r.unit ? ' ' + r.unit : ''}` : '';
                    const timeTxt = r.applied_at ? moment(r.applied_at).format('HH:mm') : '';
                    const alreadyTaken = takenTodaySchema.has(`reinf-${r.id}`);
                    options.push(
                      <Option disabled={alreadyTaken} key={`reinf-${r.id}`} value={`reinf-${r.id}`}>
                        [Refuerzo] {r.medication_name || `#${r.medication}`} {doseTxt ? `— ${doseTxt}` : ''} {timeTxt ? `— ${timeTxt}` : ''}
                      </Option>
                    );
                  });
                }
                return options;
              })()}
            </Select>
          </Form.Item>
          <div style={{ marginTop: -16, marginBottom: 16, color: '#999', fontSize: 12 }}>Los medicamentos ya registrados (tomados o no) hoy no se muestran.</div>
          {/*<Form.Item name="taken_time" label="Hora de la toma" rules={[{ required: true, message: 'Seleccione la hora' }]}>*/}
          {/*  <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />*/}
          {/*</Form.Item>*/}
          <Form.Item name="taken" label="¿Fue tomada?" initialValue={true} rules={[{ required: true, message: 'Indique si fue tomada' }]}>
            <Select>
              <Option value={true}>Sí</Option>
              <Option value={false}>No</Option>
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.taken !== curr.taken}>
            {({ getFieldValue }) => getFieldValue('taken') === false ? (
              <Form.Item name="not_taken_reason" label="Motivo" rules={[{ required: true, message: 'Ingrese el motivo de la no administración' }]}>
                <Input.TextArea rows={3} maxLength={255} />
              </Form.Item>
            ) : null}
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
}

export default MedicationIntakes;
